import {
  Archive, ArchiveRestore, MoreHorizontal, Plus, Settings2, Voicemail,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, Skeleton, TableSkeleton } from "@/components/ui/feedback";
import { CopyButton } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import { VoicemailMessageFormDialog } from "@/features/voicemail-message-form";
import { ApiError } from "@/lib/api";
import { formatClipLength } from "@/lib/audio";
import { useAuth } from "@/lib/auth";
import {
  useAudioLibrary, useTenants, useUpdateVoicemailMessage, useVoicemailLibrary,
} from "@/lib/queries";
import { atLeast, type VoicemailMessage } from "@/lib/types";
import { cn, formatDateTime } from "@/lib/utils";
import {
  LIBRARY_SHOWS, languageText, libraryRows, messageRecording, type LibraryShow, type MessageRecording,
} from "@/lib/voicemail";

/**
 * The voicemail library (ADR-019): the messages a rep can leave when a dialer call reaches a lead's
 * voicemail greeting. The rep picks one under Transfer on the phone, the dialer moves the lead's leg
 * onto it, and the rep's call ends while it plays.
 *
 * A drop names a message by its slug, so the slug is fixed at create and nothing is deleted:
 * retiring is `active: false`, which takes the message off reps' phones, and core-api refuses a drop
 * that names it, until it is restored. What a message plays is a file from the audio store, shown by
 * the name it has there rather than as the URL the switch fetches (`messageRecording`).
 *
 * Supervisors read the active library. Admins see the retired messages too, with a filter, and add,
 * edit, retire and restore. A superadmin reads every tenant's library, so for them the tenant is a
 * column.
 */
export function VoicemailMessagesPage() {
  const { user: actor } = useAuth();
  const toast = useToast();
  const canManage = atLeast(actor?.role, "admin");
  const isSuperadmin = actor?.role === "superadmin";
  const { data, isPending, error, refetch } = useVoicemailLibrary(canManage);
  // Names each message's recording. Until it has loaded a recording is not called missing.
  const audio = useAudioLibrary();
  const { data: tenants } = useTenants({ enabled: isSuperadmin });
  const update = useUpdateVoicemailMessage();

  const [show, setShow] = React.useState<LibraryShow>("all");
  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<VoicemailMessage | undefined>();
  const [retiring, setRetiring] = React.useState<VoicemailMessage | undefined>();

  const messages = data ?? [];
  const rows = libraryRows(messages, show);

  const openForm = (message?: VoicemailMessage) => {
    setEditing(message);
    setFormOpen(true);
  };

  const setActive = async (message: VoicemailMessage, active: boolean) => {
    try {
      await update.mutateAsync({ id: message.id, active });
      toast.success(active ? "Message restored" : "Message retired", message.name);
      setRetiring(undefined);
    } catch (e) {
      toast.error(
        active ? "Could not restore the message" : "Could not retire the message",
        e instanceof ApiError ? e.message : "Please try again.",
      );
    }
  };

  const newButton = canManage ? (
    <Button variant="default" onClick={() => openForm()}>
      <Plus />
      New message
    </Button>
  ) : undefined;

  return (
    <Page>
      <PageHeader
        title="Voicemail messages"
        description={
          <>
            The messages a rep can leave when a dialer call reaches a lead's voicemail. The rep picks
            one under Transfer, and their call ends while it plays. Each plays a recording from the{" "}
            <Link to="/audio" className="text-accent-11 underline-offset-4 hover:underline">
              audio store
            </Link>
            .
          </>
        }
        action={newButton}
      />

      <Card className="overflow-hidden">
        {canManage && (
          <Toolbar>
            <Select value={show} onValueChange={(v) => setShow(v as LibraryShow)}>
              <SelectTrigger className="w-40" aria-label="Show messages">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LIBRARY_SHOWS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {SHOW_TEXT[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Toolbar>
        )}

        {isPending ? (
          <TableSkeleton cols={5} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load voicemail messages"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : messages.length === 0 ? (
          <EmptyState
            icon={Voicemail}
            title="No voicemail messages yet"
            description={
              canManage
                ? "Add the messages a rep can leave on a lead's voicemail. Until there is one, a rep who reaches a greeting has nothing to leave."
                : "An admin adds the messages a rep can leave on a lead's voicemail."
            }
            action={newButton}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Voicemail}
            title={show === "retired" ? "No retired messages" : "No active messages"}
            description={
              show === "retired"
                ? "Nothing has been retired. A retired message is listed here until it is restored."
                : "Every message is retired, so reps have none to leave. Restore one to put it back on their phones."
            }
            action={
              <Button variant="outline" onClick={() => setShow("all")}>
                Show all messages
              </Button>
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Message</TableHead>
                <TableHead>Language</TableHead>
                <TableHead>Recording</TableHead>
                <TableHead>Listen</TableHead>
                <TableHead>Added</TableHead>
                {isSuperadmin && <TableHead>Tenant</TableHead>}
                {canManage && <TableHead className="w-10" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((m) => {
                const recording = messageRecording(m.media, audio.data);
                return (
                  <TableRow key={m.id} className="[&>td]:h-11">
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5">
                        <span className={cn("font-medium", m.active ? "text-text" : "text-text-muted")}>
                          {m.name}
                        </span>
                        {!m.active && (
                          <Badge tone="outline" size="sm">
                            Retired
                          </Badge>
                        )}
                      </span>
                      <span className="flex items-center gap-1 font-mono text-xs text-text-muted">
                        {m.slug}
                        <CopyButton value={m.slug} label="Copy slug" />
                      </span>
                    </TableCell>
                    <TableCell className="text-text-muted">{languageText(m.language) ?? "—"}</TableCell>
                    <TableCell className="max-w-64 text-xs">
                      <Recording recording={recording} loading={audio.isPending} />
                    </TableCell>
                    <TableCell>
                      {recording.kind === "file" ? (
                        // The file's own URL, not the message's: it is the address the store is
                        // served from now, should that have moved since the message was saved.
                        <audio
                          src={recording.file.url}
                          controls
                          preload="none"
                          aria-label={`Listen to ${m.name}`}
                          className="h-8 w-48 max-w-full"
                        />
                      ) : (
                        <span className="text-xs text-text-subtle">—</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-text-muted">
                      {formatDateTime(m.created_at)}
                    </TableCell>
                    {isSuperadmin && (
                      <TableCell className="text-xs text-text-muted">
                        {tenants?.find((t) => t.id === m.tenant_id)?.name ?? "—"}
                      </TableCell>
                    )}
                    {canManage && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${m.name}`}>
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onSelect={() => openForm(m)}>
                              <Settings2 />
                              Edit message
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {m.active ? (
                              <DropdownMenuItem onSelect={() => setRetiring(m)}>
                                <Archive />
                                Retire
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onSelect={() => void setActive(m, true)}>
                                <ArchiveRestore />
                                Restore
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <VoicemailMessageFormDialog open={formOpen} onOpenChange={setFormOpen} message={editing} />

      <ConfirmDialog
        open={Boolean(retiring)}
        onOpenChange={(open) => !open && setRetiring(undefined)}
        title="Retire this message?"
        description={
          <>
            <span className="font-medium">{retiring?.name}</span> comes off reps' phones, and a drop
            that names it is refused, until it is restored. A message already playing on a call
            finishes. Its recording stays chosen, so the file cannot be deleted from the audio store
            while this message names it.
          </>
        }
        confirmLabel="Retire message"
        loading={update.isPending}
        onConfirm={() => {
          if (retiring) void setActive(retiring, false);
        }}
      />
    </Page>
  );
}

const SHOW_TEXT: Record<LibraryShow, string> = {
  all: "All messages",
  active: "Active",
  retired: "Retired",
};

/** What a message plays, by the name it has in the audio store, as a setting's picker shows it. */
function Recording({ recording, loading }: { recording: MessageRecording; loading: boolean }) {
  switch (recording.kind) {
    case "file":
      return (
        <>
          <p className="truncate text-sm text-text">{recording.file.name}</p>
          <p className="tabular-nums text-text-muted">{formatClipLength(recording.file.duration_ms)}</p>
        </>
      );
    case "unread":
      return loading ? (
        <Skeleton className="h-3 w-28" />
      ) : (
        <span className="text-text-muted">A file from the audio store</span>
      );
    case "missing":
      return <span className="text-warning-11">A file that is no longer in the audio store</span>;
    case "other":
      return (
        <p className="truncate text-text-muted" title={recording.value}>
          On the switch: <span className="font-mono">{recording.value}</span>
        </p>
      );
    case "none":
      return <span className="text-text-subtle">—</span>;
  }
}
